import { EntityAssignmentType } from 'constants/taskCategories';
import { TaskAssignmentType } from 'constants/tasks';

const getBorrowerName = (borrowers, borrowerId) =>
  borrowers.find(({ value }) => value === borrowerId)?.name;

const withOwnerLabel = (entities, getOwnerName) =>
  entities.map(entity => {
    const ownerName = getOwnerName(entity);

    return {
      ...entity,
      typeAndAmount: ownerName ? `${ownerName} — ${entity.typeAndAmount}` : entity.typeAndAmount,
    };
  });

// The task-creation engine derives an income-scoped task's borrower from the income's owner
// across all borrowers (Skynet.Loans FH-1730), so every income/asset on the loan is offered
// regardless of the picked assignee — each labelled with its owner to stay distinguishable.
export const getTaskEntityOptions = ({ entityAssignmentType, borrowers, incomes, assets }) => {
  switch (entityAssignmentType) {
    case EntityAssignmentType.BORROWER:
      return borrowers;

    case EntityAssignmentType.INCOME:
      return withOwnerLabel(incomes, ({ borrowerId }) => getBorrowerName(borrowers, borrowerId));

    case EntityAssignmentType.ASSET:
      return withOwnerLabel(assets, ({ owners }) =>
        owners
          .map(ownerId => getBorrowerName(borrowers, ownerId))
          .filter(Boolean)
          .join(', '),
      );

    default:
      return null;
  }
};

export const getIncomeOwnerOption = (income, borrowers) =>
  income ? (borrowers.find(({ value }) => value === income.borrowerId) ?? null) : null;

const SHARED_APPLICATION_GROUP = 'shared';

const getBorrowerApplicationGroupKey = ({ isSeparateApplication, value }) =>
  isSeparateApplication ? value : SHARED_APPLICATION_GROUP;

// A separate-application co-borrower's file is private to them: pairing one with an assignee from
// another group routes their task to someone outside the fence. The matching server-side rejection
// ships with Skynet.Loans #2879 — until that is live on dev this fence is client-side only.
export const areBorrowerOptionsCompatible = (borrower, otherBorrower) =>
  getBorrowerApplicationGroupKey(borrower) === getBorrowerApplicationGroupKey(otherBorrower);

export const getCompatibleBorrowerOptions = (borrowers, anchorBorrower) =>
  anchorBorrower?.value
    ? borrowers.filter(borrower => areBorrowerOptionsCompatible(borrower, anchorBorrower))
    : borrowers;

/**
 * The single assignee a task can legally carry, or `null` when a real choice remains. Only a
 * separate-application entity forces one, because its group is exactly itself — a shared-group
 * entity always leaves a choice, even on a loan that happens to have one borrower today.
 */
export const getSoleLegalAssignee = (borrowers, entityOption, assigneeOption) => {
  if (!entityOption?.isSeparateApplication) {
    return null;
  }

  const compatibleOptions = getCompatibleBorrowerOptions(borrowers, entityOption);
  const isAssigneeOpenOrMatching =
    !assigneeOption?.value || assigneeOption.value === entityOption.value;

  return compatibleOptions.length === 1 && isAssigneeOpenOrMatching ? compatibleOptions[0] : null;
};

// An incompatible pair now persists, so the assignee can sit outside its own filtered list.
// AutocompleteCDS sets no isOptionEqualToValue, so MUI matches options by reference and renders the
// field blank — which would read as the silent discard this validation exists to remove.
export const getOptionsWithSelected = (options, selectedOption) =>
  !selectedOption?.value || options.some(({ value }) => value === selectedOption.value)
    ? options
    : [...options, selectedOption];

// The component gate and the schema's attach condition must agree: if one says the pair is checked
// and the other does not, either a submitted error never clears or yup validates a path its schema
// never declared. Deriving both from here is what keeps them from drifting apart.
export const checkIsBorrowerPairValidated = taskType =>
  taskType?.entityAssignmentType === EntityAssignmentType.BORROWER &&
  taskType?.taskAssignmentType !== TaskAssignmentType.SLA_GROUP;

/**
 * @returns the message naming whichever side files a separate application, or `null` when the
 * pair is valid or either side is still unset.
 */
export const getBorrowerPairError = (entityOption, assigneeOption) => {
  if (
    !entityOption?.value ||
    !assigneeOption?.value ||
    areBorrowerOptionsCompatible(entityOption, assigneeOption)
  ) {
    return null;
  }

  return assigneeOption.isSeparateApplication
    ? `${assigneeOption.name} files a separate application and can only be assigned tasks about themselves.`
    : `${entityOption.name} files a separate application — not valid for assignee ${assigneeOption.name}.`;
};
